import React, { useEffect, useState } from 'react'
import styles from '@/src/components/scss/Calendar.module.scss'
import { Box, Collapse, styled, useMediaQuery } from '@mui/material'
import { Typography, IconButton, Datepicker } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import ArrowRight24 from '@evocolabs/icons/lib/ArrowRight24'
import ChevronDownSmall24 from '@evocolabs/icons/lib/ChevronDownSmall24'

const Wrapper = styled(Collapse)`
  background-color: ${theme.palette.white.cold};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  ${(props) => props.theme.breakpoints.down('md')} {
    max-width: unset;
    padding: 16px 8px;
    ${(props) => (props.in ? '' : 'padding-top: 8px;')}
  }
`

const dayList = [
  {
    name: 'Sun',
    color: theme.palette.primary[600]
  },
  {
    name: 'Mon',
    color: theme.palette.grey[700]
  },
  {
    name: 'Tue',
    color: theme.palette.grey[700]
  },
  {
    name: 'Wed',
    color: theme.palette.grey[700]
  },
  {
    name: 'Thu',
    color: theme.palette.grey[700]
  },
  {
    name: 'Fri',
    color: theme.palette.grey[700]
  },
  {
    name: 'Sat',
    color: theme.palette.primary[600]
  }
]
const monthList = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

function floorToDate(date) {
  return new Date(date).setHours(0, 0, 0, 0)
}
const TZOffset = new Date(Date.now()).getTimezoneOffset() / 60

const Calendar = ({
  timeZone,
  collapse,
  setCollapse,
  selection,
  onSelect,
  onSwitchMonth
}) => {
  const usePC = useMediaQuery(theme.breakpoints.up('sm'))
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth()) // for limit back month
  const [endMonth, setEndMonth] = useState(
    new Date(
      floorToDate(
        new Date(Date.now()).setDate(new Date(Date.now()).getDate() + 15)
      )
    ).getMonth()
  ) // for limit next month
  const [date, setDate] = useState({
    //for select current date in calendar depending on timezone
    now: Date.now(),
    expiration: floorToDate(
      new Date(Date.now()).setDate(new Date(Date.now()).getDate() + 15)
    )
  })
  const [calendarData, setCalendarData] = useState({
    // month and year for calendar now page time
    date: new Date(date.now).getDate(),
    month: new Date(date.now).getMonth(),
    year: new Date(date.now).getFullYear()
  })
  const [dateList, setDateList] = useState(
    Array.from(
      {
        length: new Date(calendarData.year, calendarData.month + 1, 0).getDate()
      },
      (_, i) => i + 1
    )
  )
  const getCurrentDateTimeSlotsWithTimeZone = (timeZone) => {
    return +new Date(
      new Date().toLocaleDateString('en-US', {
        timeZone: timeZone
      }) +
        ' ' +
        new Date().toLocaleTimeString('en-US', {
          timeZone: timeZone
        })
    )
  }
  useEffect(() => {
    setDate({
      now: getCurrentDateTimeSlotsWithTimeZone(timeZone),
      expiration: floorToDate(
        new Date(getCurrentDateTimeSlotsWithTimeZone(timeZone)).setDate(
          new Date(getCurrentDateTimeSlotsWithTimeZone(timeZone)).getDate() + 15
        )
      )
    })
  }, [timeZone])

  function getDay(inputDate) {
    return new Date(calendarData.year, calendarData.month, inputDate).getDay()
  }
  function isToday(inputDate) {
    const now = new Date(date.now)
    const calendarDate = new Date(
      calendarData.year,
      calendarData.month,
      inputDate
    )
    return (
      calendarDate.getFullYear() === now.getFullYear() &&
      calendarDate.getMonth() === now.getMonth() &&
      calendarDate.getDate() === now.getDate()
    )
  }
  function isDisabledTime() {
    const possibleTime = possibleTimeRef.current
    const list = []
    for (let i = 0; i < possibleTime.length; i++) {
      const timeObject = {
        ..._selection,
        start: possibleTime[i].start,
        end: possibleTime[i].end
      }
      list.push(
        availableTime.findIndex(
          (e) =>
            e.date === timeObject.date &&
            e.month === timeObject.month &&
            e.year === timeObject.year &&
            e.start <= timeObject.start &&
            e.end >= timeObject.end
        ) + 1
      )
    }
    return list
  }

  function switchMonth(direction) {
    if (direction === 'back') {
      const now = new Date(calendarData.year, calendarData.month, 1)
      console.log(date[now.getMonth()])
      const last = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      setCalendarData({
        ...calendarData,
        month: last.getMonth(),
        year: last.getFullYear()
      })
      onSwitchMonth({ year: last.getFullYear(), month: last.getMonth() })
    } else {
      const now = new Date(calendarData.year, calendarData.month, 1)
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1)
      setCalendarData({
        ...calendarData,
        month: next.getMonth(),
        year: next.getFullYear()
      })
      onSwitchMonth({ year: next.getFullYear(), month: next.getMonth() })
    }
  }
  const [_selection, _setSelection] = useState({
    date: null,
    month: null,
    year: null
  })
  function isDisabledDate(inputDate) {
    return (
      Date.UTC(calendarData.year, calendarData.month, inputDate, TZOffset) <
        floorToDate(date.now) ||
      Date.UTC(calendarData.year, calendarData.month, inputDate, TZOffset) >=
        date.expiration
    )
  }

  function handleSelect(item, ...args) {
    _setSelection({
      date: item,
      month: calendarData.month,
      year: calendarData.year
    })
    if (onSelect) {
      onSelect(
        {
          date: item,
          month: calendarData.month,
          year: calendarData.year
        },
        ...args
      )
    }
  }

  return (
    <Wrapper in={!collapse} collapsedSize="56px">
      <Box className={styles.titleWrapper}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          {collapse && (selection ? selection.date : _selection.date) + ' '}
          {`${monthList[calendarData.month]} ${calendarData.year}`}
        </Typography>
        <Box className={styles.switchWrapper}>
          {collapse ? (
            <IconButton onClick={() => setCollapse(false)}>
              <ChevronDownSmall24 />
            </IconButton>
          ) : (
            <>
              <IconButton
                onClick={() => switchMonth('back')}
                disabled={calendarData.month <= currentMonth}
              >
                <Box sx={{ transform: 'rotate(180deg)', display: 'flex' }}>
                  <ArrowRight24
                    forceStroke={
                      calendarData.month <= currentMonth ? '#B8B6B5' : '#1A1A1A'
                    }
                  />
                </Box>
              </IconButton>
              <IconButton
                onClick={() => switchMonth('next')}
                disabled={calendarData.month >= endMonth}
              >
                <ArrowRight24
                  forceStroke={
                    calendarData.month >= endMonth ? '#B8B6B5' : '#1A1A1A'
                  }
                />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <Box className={styles.dayWrapper}>
        {dayList.map((item, idx) => (
          <Box key={item.name} sx={{ gridColumn: idx + 1 }} textAlign="center">
            <Typography
              variant="caption"
              sx={{ color: item.color, textAlign: 'center', fontWeight: 500 }}
            >
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box className={styles.dateWrapper}>
        {dateList.map((item) => (
          <Box
            key={item}
            sx={{ textAlign: 'center', gridColumn: getDay(item) + 1 }}
          >
            <Datepicker
              today={isToday(item)}
              disabled={isDisabledDate(item)}
              clicked={
                selection
                  ? selection.date === item &&
                    selection.month === calendarData.month &&
                    selection.year === calendarData.year
                  : _selection.date === item &&
                    _selection.month === calendarData.month &&
                    _selection.year === calendarData.year
              }
              size={usePC ? '' : 'small'}
              onClick={() => handleSelect(item)}
            >
              {item}
            </Datepicker>
          </Box>
        ))}
      </Box>
    </Wrapper>
  )
}

export default Calendar
